/**
 * Permission ids follow `<module>.<resource>.<action>`.
 *
 * - `module`   who owns the resource: `core` or a plugin id (`dealership`, `courses`…).
 * - `resource` the thing being protected, unique inside the module (`organization`).
 * - `action`   what is allowed: a standard action or a custom verb (`export`, `assign-owner`).
 *
 * The CASL subject is `<module>.<resource>`, so a check is always "can <action> on <subject>".
 */
export const STANDARD_ACTIONS = ['view', 'create', 'update', 'delete', 'manage'] as const;

export type StandardAction = (typeof STANDARD_ACTIONS)[number];

export type PermissionId = `${string}.${string}.${string}`;

export type PermissionSubject = `${string}.${string}`;

export interface ParsedPermissionId {
  readonly id: PermissionId;
  readonly module: string;
  readonly resource: string;
  readonly action: string;
  readonly subject: PermissionSubject;
}

/** One segment: lowercase kebab-case, starting with a letter. */
const SEGMENT = /^[a-z][a-z0-9-]*$/;

export function isValidSegment(value: string): boolean {
  return SEGMENT.test(value);
}

export function isPermissionId(value: string): value is PermissionId {
  return parsePermissionId(value) !== null;
}

/** Splits a permission id into its parts; `null` when it is malformed. */
export function parsePermissionId(value: string): ParsedPermissionId | null {
  const parts = value.split('.');
  if (parts.length !== 3) return null;
  const [module, resource, action] = parts as [string, string, string];
  if (!isValidSegment(module) || !isValidSegment(resource) || !isValidSegment(action)) {
    return null;
  }
  return {
    id: value as PermissionId,
    module,
    resource,
    action,
    subject: `${module}.${resource}`,
  };
}
